import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowUpRight } from 'lucide-react';
import { EASE } from './Reveal';
import { getLenis } from '@/lib/smooth';
import { ReelPlayer } from './ReelPlayer';
import { CaseGallery } from './CaseGallery';
import { AudioPlayer } from './AudioPlayer';

export const CaseModal = ({ work, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    if (work) {
      document.addEventListener('keydown', onKey);
      getLenis()?.stop();
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      getLenis()?.start();
    };
  }, [work, onClose]);

  const stacked = work?.galleryLayout === 'stacked';

  return (
    <AnimatePresence>
      {work && (
        <motion.div
          data-testid="case-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ y: 80, opacity: 0, scale: 0.7 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.5, ease: EASE }}
            className={`relative bg-cream border border-ink rounded-[1.5rem] w-full max-h-[85vh] overflow-hidden shadow-[10px_10px_0_#1A1A1A] flex flex-col ${
              stacked ? 'max-w-4xl' : 'max-w-5xl md:flex-row'
            }`}
          >
            <div className={stacked ? `relative shrink-0 bg-ink ${work.galleryAspect || 'aspect-[2000/700] sm:aspect-[2000/620]'}` : `relative shrink-0 h-64 sm:h-72 md:h-auto md:w-1/2 md:self-stretch md:min-h-[420px]`}>
              {work.gallery ? (
                <CaseGallery images={work.gallery} fit={work.galleryFit} alt={`${work.client} — ${work.title}`} />
              ) : work.audio ? (
                <AudioPlayer audio={work.audio} poster={work.image} title={`${work.client} — ${work.title}`} />
              ) : work.reel ? (
                <ReelPlayer reel={work.reel} title={`${work.client} — ${work.title} showreel`} className="absolute inset-0" />
              ) : (
                <img src={work.image} alt={`${work.client} — ${work.title}`} className="absolute inset-0 w-full h-full object-cover" />
              )}
              <span className={`absolute left-4 bg-butter text-ink text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 border border-ink ${work.reel?.type === 'instagram' ? 'bottom-4' : 'top-4'}`}>
                {work.category}
              </span>
            </div>

            <div className={`p-6 sm:p-10 overflow-y-auto min-h-0 flex-1 ${stacked ? '' : 'md:w-1/2'}`} data-lenis-prevent>
              <p className="text-[11px] uppercase tracking-[0.25em] text-ink/55 mb-2">
                {work.client} — {work.year}
              </p>
              <h3 className="font-slab font-black text-3xl sm:text-4xl leading-tight mb-4">{work.title}</h3>
              <p className="text-ink/80 leading-relaxed mb-7">{work.summary}</p>

              <p className="text-[11px] uppercase tracking-[0.25em] text-royal font-bold mb-2">The Challenge</p>
              <p className="text-ink/75 leading-relaxed mb-6">{work.challenge}</p>

              <p className="text-[11px] uppercase tracking-[0.25em] text-royal font-bold mb-2">The Butter Treatment</p>
              <p className="text-ink/75 leading-relaxed mb-6">{work.solution}</p>

              <div className="flex flex-wrap gap-2 mb-7">
                {work.deliverables.map((d) => (
                  <span key={d} className="text-xs font-medium border border-ink/25 rounded-full px-3.5 py-1.5 text-ink/75">
                    {d}
                  </span>
                ))}
              </div>

              <div className="bg-butter border border-ink rounded-xl px-5 py-4 font-slab font-bold text-sm sm:text-base">
                {work.outcome}
              </div>

              {work.link && (
                <a
                  href={work.link.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="case-live-link"
                  className="group mt-5 inline-flex items-center gap-2 font-slab font-bold text-sm border-b-2 border-ink pb-0.5 hover:text-royal hover:border-royal transition-colors"
                >
                  {work.link.label}
                  <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-300" />
                </a>
              )}
            </div>

            <button
              data-testid="modal-close-button"
              onClick={onClose}
              aria-label="Close case study"
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-ink text-cream flex items-center justify-center hover:bg-royal transition-colors"
            >
              <X size={18} />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
